import {
  getBoolText,
  getContractingAddressName,
  getContractingCommodity,
  getTypedStepValues,
} from "features/contracting/helpers/contractingFormHelper";
import ContractingStepSummaryProps from "features/contracting/models/ContractingStepSummaryProps";
import PlaceOfDeliveryStep from "features/contracting/models/steps/placeOfDelivery/PlaceOfDeliveryStep";
import PageCard from "features/shared/components/card/PageCard";
import CheckValueInfoItem from "features/shared/components/infoItem/CheckValueInfoItem";
import RenderWrapper from "features/shared/components/renderWrapper/RenderWrapper";
import _ from "lodash";
import React from "react";
import { useTranslation } from "react-i18next";
import ContractingAddressSummary from "./ContractingAddressSummary";
import {
  getPlaceOfDeliveryTrans,
  getYearlyConsumptionTrans,
  isMultipleDistributionRate,
} from "features/contracting/sections/contractingForms/placeOfDeliveryForm/placeOfDeliveryFormHelper";
import { formatNumber } from "features/shared/helpers/Formatters";
import { Commodity } from "features/shared/enums/commodity";
import { useTranslationWithPrefix } from "features/shared/hooks/useTranslationWithPrefix";

const PlaceOfDeliverySummary = ({
  contracting,
  step,
  configuration,
}: ContractingStepSummaryProps) => {
  // translations
  const tBool = useTranslation("shared\\enums\\boolEnum").t;
  const tPhases = useTranslation("placeOfDelivery\\enums\\phases").t;
  const tAddress = useTranslation(
    "contracting\\enums\\contractingAddressType"
  ).t;
  const tLabels = useTranslationWithPrefix("contracting\\contracting", "placeOfDeliverySummary");
  const tLabelsForm = useTranslationWithPrefix("contracting\\contracting", "placeOfDeliveryDataSubForm");

  // private props
  const values = getTypedStepValues<PlaceOfDeliveryStep>(
    contracting,
    step.component
  );
  const commodity = getContractingCommodity(contracting);

  // helperes
  const isPlaceOfDeliveryAddress = () => {
    return values.placeOfDeliveryAddressIsSameAsMain === false;
  };

  const formatConsumption = (value: number) => {
    return formatNumber(value, 3, "MWh");
  };

  const formatCircuitBreaker = (value: number) => {
    return formatNumber(value, 2, "A");
  };

  const isElectricity = () => {
    return commodity === Commodity.Electricity;
  };

  const isGas = () => {
    return commodity === Commodity.Gas;
  };

  const getConsumptionCharacteristicNames = () => {
    const characteristics = values.consumptionCharacteristics;

    if (characteristics && characteristics.length > 0) {
      let names: string[] = [];
      for (const code of characteristics) {
        const configCharacteristic = configuration.consumptionCharacteristics?.find(
          (x) => x.code === code
        );

        if (configCharacteristic) {
          names.push(configCharacteristic.name!);   
        }
      }
      
      return names.join(", ");
    }

    return undefined;
  };

  const translatePhase = (value: number) => {
    return tPhases(String(value));
  };

  // render
  return (
    <>
      <PageCard title={tLabels("cardTitle")} bodyClassName="px-4 py-3">
        <CheckValueInfoItem
          title={getPlaceOfDeliveryTrans(commodity, tLabelsForm, "label")}
          value={values.placeOfDeliveryIdentification}
        />
        {/* Electricity */}
        <RenderWrapper isVisible={isElectricity}>
          <CheckValueInfoItem
            title={tLabels("distributionRate")}
            value={values.distributionRate}
          />
        </RenderWrapper>
        {/* Gas */}
        <RenderWrapper isVisible={isGas}>
          <CheckValueInfoItem
            title={tLabels("consumptionCharacteristics")}
            value={getConsumptionCharacteristicNames()}
          />
        </RenderWrapper>
        <CheckValueInfoItem
          title={getYearlyConsumptionTrans(commodity, tLabelsForm, "label")}
          value={values.yearlyConsumption}
          formatValue={formatConsumption}
        />
        {/* Electricity */}
        <RenderWrapper isVisible={isElectricity}>
          <RenderWrapper
            isVisible={() =>
              isMultipleDistributionRate(
                commodity,
                values.distributionRate!,
                configuration.distributionTariffs!
              )
            }
          >
            <CheckValueInfoItem
              title={tLabels("yearlyConsumptionNT")}
              value={values.yearlyConsumptionNT}
              formatValue={formatConsumption}
            />
          </RenderWrapper>
          <CheckValueInfoItem
            title={tLabels("phase")}
            value={values.phase}
            formatValue={translatePhase}
          />
          <CheckValueInfoItem
            title={tLabels("circuitBreaker")}
            value={values.circuitBreaker}
            formatValue={formatCircuitBreaker}
          />
        </RenderWrapper>
      </PageCard>
      <PageCard title={tLabels("addressCardTitle")} bodyClassName="px-4 py-3">
        <CheckValueInfoItem
          title={getContractingAddressName(
            contracting,
            tAddress,
            true,
            tLabels("placeOfDeliveryAddressIsSameAsMain")
          )}
          value={values.placeOfDeliveryAddressIsSameAsMain}
          formatValue={(x) => getBoolText(x, tBool)}
          isEmpty={_.isNil}
        />
        <RenderWrapper isVisible={isPlaceOfDeliveryAddress}>
          <CheckValueInfoItem
            title={tLabels("placeOfDeliveryAddressHasCadastralInfo")}
            value={values.placeOfDeliveryAddressHasCadastralInfo}
            formatValue={(x) => getBoolText(x, tBool)}
            isEmpty={_.isUndefined}
          />
          <ContractingAddressSummary
            address={values.placeOfDeliveryAddress ?? {}}
            hasCadastralInfo={values.placeOfDeliveryAddressHasCadastralInfo}
          />
        </RenderWrapper>
      </PageCard>
    </>
  );
};

export default PlaceOfDeliverySummary;
